import { isFunction } from 'lodash'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import sleep from 'sleep-promise'
import { v4 as uuid } from 'uuid'

import { usePrevious } from '../../hooks'
import FieldComponent from './FieldComponent'
import { TField, TValue } from './types'
import useField from './useField'
import { getFeedback } from './utils'
import { required } from './Validation'

interface IAsyncValidationField extends TField {
  debounceDelay?: number
  validate?: (val: TValue) => Promise<any>
}

const fakeNoop = async (): Promise<boolean> => {
  return new Promise((resolve) => false)
}

const AsyncValidationField = (
  props: IAsyncValidationField,
): React.ReactElement => {
  const {
    name,
    validate = fakeNoop,
    children,
    debounceDelay = 500,
    isRequired,
    ...rest
  } = props

  const [localValue, setLocalValue] = useState()
  const lock = useRef<string>()
  const lastValue = useRef()
  const lastValidation = useRef<any>()
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef()

  const validateSync = (v): any => {
    // const res = required(v)
    // if (res) {
    //   return res
    // }
    return lastValidation.current
  }

  const {
    input,
    input: { value, onChange, onBlur },
    meta,
    meta: { validating, error },
  } = useField(name, {
    ...rest,
    raw: true,
    validate: validateSync,
    subscribeAll: true,
  })

  const previousValue = usePrevious(value)

  useEffect(() => {
    if (previousValue === localValue && previousValue !== value) {
      setLocalValue(value)
    }
  }, [value])

  const { feedback, feedbackType } = getFeedback(meta)
  // console.log('async field', { value })
  const validateAsync = async (value: TValue): Promise<void> => {
    // console.log('validate', value)
    // if (isFunction(props.validate)) {
    //   setIsLoading(false)
    //   console.log("validate is function so change value")
    //   // onChange(value)
    //   return
    // }

    const id = uuid()
    lock.current = id
    if (lastValue.current === value) {
      return
    }
    lastValue.current = value
    setIsLoading(true)
    await sleep(debounceDelay)
    if (lock.current !== id) {
      return
    }
    // console.log('actually validating async', value)
    const result = await validate(value)
    if (lock.current !== id) {
      return
    }
    setIsLoading(false)
    lastValidation.current = result
    onChange(value)
    onBlur()
  }

  useEffect(() => {
    validateAsync(localValue)
  }, [localValue])

  useEffect(() => {
    lastValidation.current = isFunction(validate) ? error : undefined
    setLocalValue(value)
  }, [])

  const childProps = {
    ...input,
    value: localValue,
    onChange: setLocalValue,
    ref: inputRef,
    isInvalid: feedbackType === 'invalid',
    isLoading: validating || isLoading || props.isLoading,
  }

  let inner: ReactNode

  if (children) {
    if (isFunction(children) && React.isValidElement(children(childProps))) {
      inner = children(childProps)
    } else {
      inner = React.cloneElement(children as React.ReactElement, childProps)
    }
  }

  return (
    <FieldComponent {...rest} feedback={feedback} feedbackType={feedbackType}>
      {inner}
    </FieldComponent>
  )
}

export default AsyncValidationField
